// Active workspace's data-backend config (Phase C). Set synchronously by configureBackend (from the
// Redis workspace meta) before App renders. Kept in its own tiny module so both src/data/index.js
// (factory selection) and SupabaseDataService (space-scoped queries) can read it without a cycle.
//
//   primaryBackend — 'supabase' (default) | 'airtable'
//     Flipped 2026-07-30. This is the FALLBACK only — a space whose workspace meta carries an
//     explicit `primary_backend` still gets exactly what it asks for, so Airtable spaces are
//     unaffected and migrate one at a time by flipping their own meta. The fallback now favours
//     Supabase so new and unconfigured spaces land on the RLS-enforced path by default.
//   airtableSync   — 'off' (default, clean-flip) | 'dual-write'
//   spaceId        — the Supabase spaces.id (uuid) for this slug; needed to scope item/link/note
//                    queries (RLS gates *visibility*, but we still filter to the one space). Carried
//                    on the workspace meta as `supabase_space_id`, set at cutover.

const _cfg = { primaryBackend: 'supabase', airtableSync: 'off', spaceId: null, spaceSlug: null };

export function setBackendConfig({ primaryBackend, airtableSync, spaceId, spaceSlug } = {}) {
  _cfg.primaryBackend = primaryBackend || 'supabase';
  _cfg.airtableSync = airtableSync || 'off';
  _cfg.spaceId = spaceId ?? null;
  if (spaceSlug !== undefined) _cfg.spaceSlug = spaceSlug ?? null;
}

// ── Space scopes ──────────────────────────────────────────────────────────────────────────────
// A page that boots no space — My Arcos (/me) — still has moments that work inside one: a write into
// a chosen space (meData `inSpace`), or an item's Edit form opened from a popup (SpaceScope.jsx). A
// scope makes that space the active one for as long as it is held, OVER whatever setBackendConfig
// last said — which matters because the router calls setBackendConfig during every render, and on
// /me that resets the space to null mid-form (a session refresh is enough). Scopes stack, and each
// releases only itself, so an `inSpace` write inside an open form puts the form's space back.
const _scopes = [];

/** Hold a space active until the returned release is called. */
export function pushSpaceScope({ spaceId = null, spaceSlug = null } = {}) {
  const scope = { spaceId, spaceSlug };
  _scopes.push(scope);
  return () => {
    const i = _scopes.lastIndexOf(scope);
    if (i >= 0) _scopes.splice(i, 1);
  };
}

const top = () => (_scopes.length ? _scopes[_scopes.length - 1] : null);

export function getBackendConfig() {
  const scope = top();
  return scope ? { ..._cfg, primaryBackend: 'supabase', spaceId: scope.spaceId, spaceSlug: scope.spaceSlug } : { ..._cfg };
}

export function getActiveSpaceId() {
  const scope = top();
  return scope ? scope.spaceId : _cfg.spaceId;
}
export function getActiveSpaceSlug() {
  const scope = top();
  return scope ? scope.spaceSlug : _cfg.spaceSlug;
}

// The active space's display name, for anything outside React that needs to name the space an item
// belongs to — the Table's "Space" column, and the filters that read the same column. Set by
// EditContext from the workspace meta.
let _spaceName = null;
export function setActiveSpaceName(name) {
  _spaceName = name || null;
}
export function getActiveSpaceName() {
  return _spaceName;
}
